import { useState, useRef, forwardRef, useImperativeHandle } from "react";
import { Button, Col, Row } from "reactstrap";
import getSymbolFromCurrency from "currency-symbol-map";
import Currency from "../Inputs/Currency";
import DisplayRecord from "./DisplayRecord";
import { Popover, Whisper } from "rsuite";
import AppSpinner from "../../elements/AppSpinner";
import EmptyField from "./EmptyField";

const CurrencyRecord = (
  {
    field,
    recordID,
    existingValue,
    onChange,
    inputSize,
    mode,
    tableView,
    modifyTrigger,
    onUpdate,
    submitting,
    error,
  },
  ref
) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [value, setValue] = useState(null);
  const [currencyCode, setCurrencyCode] = useState("USD");
  const whisperRef = useRef();

  const startEditing = () => {
    setIsEditing(true);
    setValue(existingValue?.Value);
    setCurrencyCode(
      existingValue && existingValue.Currency ? existingValue.Currency : "USD"
    );
    whisperRef.current?.open();
  };

  const save = (e) => {
    e.preventDefault();

    if (value && isNaN(value)) {
      return setLocalError("The value for this field must be numeric.");
    }

    let val = null;

    if (value !== "") {
      val = {
        Value: value,
        Currency: currencyCode,
        Symbol: getSymbolFromCurrency(currencyCode),
      };
    }

    onUpdate({
      field,
      recordID,
      payload: { [field.APIName]: val },
      onSuccess: stopEditing,
    });
  };

  const stopEditing = () => {
    setIsEditing(false);
    setValue(null);
    whisperRef?.current?.close();
  };

  const currencyToReadable = (val) => {
    if (!val || val.Value === null) {
      return <EmptyField table={tableView} />;
    }

    if (!val.Currency) {
      return "(unknown currency)";
    }

    let currency = getSymbolFromCurrency(val.Currency);

    if (!currency) {
      return "(unknown currency)";
    }

    let formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: val.Currency,
    });

    return formatter.format(parseFloat(val.Value));
  };

  const handleChange = (val) => {
    if (!val) {
      setValue(null);
      setCurrencyCode(null);

      return;
    }

    setValue(val.Value);
    setCurrencyCode(val.Currency);
  };

  useImperativeHandle(ref, () => ({
    focus: () => {
      whisperRef.current?.open();
    },
  }));

  return (
    <>
      <Whisper
        preventOverflow
        ref={whisperRef}
        placement="auto"
        trigger={isEditing ? "click" : "none"}
        speaker={
          <Popover
            className="sidebar-popover p-0 m-0"
            style={{ width: "350px" }}
          >
            <div className="cursor-pointer p-2 text-dark border-bottom border-superlight">
              <Row className="align-items-center">
                <Col xs="">
                  <h5 className="text-dark m-0">{field?.Name}</h5>
                </Col>
                <Col xs="auto">
                  <Button
                    size="sm"
                    outline
                    color="secondary"
                    onClick={(e) => {
                      e.preventDefault();

                      stopEditing();
                    }}
                  >
                    Done
                  </Button>
                </Col>
              </Row>
            </div>
            <div className="p-2">
              <Currency
                existingValue={existingValue ?? ""}
                field={field}
                inputSize={inputSize}
                onChange={handleChange}
              ></Currency>
            </div>

            <div className="text-right px-2 pb-2 pt-0">
              <Button
                disabled={(field?.Required && !value) || submitting}
                size="sm"
                color="primary"
                className="btn-icon-only"
                onClick={save}
              >
                {submitting ? (
                  <AppSpinner />
                ) : (
                  <i
                    className="mdi mdi-check"
                    color="white"
                    style={{ fontSize: "20px" }}
                  ></i>
                )}
              </Button>
            </div>
            {(submitting || error) && (
              <div className="p-2 mb-0 text-left border-top border-superlight">
                <p className="m-0 text-danger">
                  {error && (
                    <small>Unable to save your changes. Try again.</small>
                  )}
                  {localError && <small>{localError}</small>}
                </p>
              </div>
            )}
          </Popover>
        }
      >
        <div>
          <DisplayRecord
            mode={mode}
            tableView={tableView}
            modifyTrigger={modifyTrigger}
            openModify={startEditing}
            isSensitive={field?.Sensitive ?? false}
            displayComponent={(any, innerRef) => (
              <div ref={innerRef}>{currencyToReadable(existingValue)}</div>
            )}
          />
        </div>
      </Whisper>
    </>
  );
};

export default forwardRef(CurrencyRecord);
